import { useAuth0 } from "ct-auth"
import HelpOutlineIcon from "@mui/icons-material/HelpOutline"
import { IconButton, Tooltip } from "@mui/material"
import { useLocation } from "react-router-dom"
import { isManager, isSponsor } from "../../../data-utils/authUtils"
import { resolveHelp, routeKeyForPath } from "../../../data-utils/helpContent"

const roleForUser = (user) => (isManager(user) ? "manager" : isSponsor(user) ? "sponsor" : "player")

/**
 * Inline "?" help affordance meant to sit next to a page/section title. Clicking it opens the
 * page-help drawer via the `ct:open-help` window event (HelpCenter listens for it). It renders
 * NOTHING when the current route has no help authored, so it never opens an empty drawer.
 *
 * This is the anchored replacement for the old floating help button — help lives in a
 * predictable place beside the title and never hovers over content or action buttons.
 *
 * `routeKey` optionally overrides the pathname-derived key. Same-URL surfaces that need
 * DIFFERENT help (e.g. solo vs. collaborative gameplay both live under /new-experience) pass
 * the right key explicitly; it is forwarded to HelpCenter via the open event so the drawer
 * shows the matching content + video.
 */
export default function HelpTip({ size = "small", className, sx, routeKey: routeKeyOverride }) {
  const { pathname } = useLocation()
  const { user } = useAuth0()
  const routeKey = routeKeyOverride ?? routeKeyForPath(pathname)
  const entry = resolveHelp(routeKey, roleForUser(user))
  if (!entry) return null
  return (
    <Tooltip title="How to use this page">
      <IconButton
        aria-label="How to use this page"
        size={size}
        className={className}
        onClick={() => window.dispatchEvent(new CustomEvent("ct:open-help", { detail: { routeKey } }))}
        sx={{ color: "var(--ct-text-muted, inherit)", ...sx }}
      >
        <HelpOutlineIcon fontSize="inherit" />
      </IconButton>
    </Tooltip>
  )
}
